import { VideoSDK } from '@videosdk.live/js-sdk';
import { useEffect, useRef, useCallback } from 'react';

const NOOP = () => {};

/**
 *
 * @param {{
 *  onDeviceChanged?: Function | undefined,
 * }}
 *
 * @returns {{
 *   getDevices: () => Promise<Array<DeviceInfo>>;
 *   getCameras: () => Promise<Array<CameraDeviceInfo>>;
 *   getMicrophones: () => Promise<Array<MicrophoneDeviceInfo>>;
 *   getPlaybackDevices: () => Promise<Array<PlaybackDeviceInfo>>;
 *   checkPermissions: (permissions? : Permission) => Promise<Map<string, boolean>>;
 *   requestPermission: (permissions? : Permission) => Promise<Map<string, boolean>>;
 * }}
 *
 */
const useMediaDevice = ({ onDeviceChanged = NOOP } = {}) => {
  const onDeviceChangedRef = useRef(onDeviceChanged);
  onDeviceChangedRef.current = onDeviceChanged;
  /**
   *
   * @returns {Promise<Array<DeviceInfo>>}
   */
  const getDevices = useCallback(async () => VideoSDK.getDevices(), []);

  /**
   *
   * @returns {Promise<Array<CameraDeviceInfo>>}
   */
  const getCameras = useCallback(async () => VideoSDK.getCameras(), []);

  /**
   *
   * @returns {Promise<Array<MicrophoneDeviceInfo>>}
   */
  const getMicrophones = useCallback(async () => VideoSDK.getMicrophones(), []);

  /**
   *
   * @returns {Promise<Array<PlaybackDeviceInfo>>}
   */
  const getPlaybackDevices = useCallback(
    async () => VideoSDK.getPlaybackDevices(),
    []
  );

  /**
   *
   * @param {permissions?: Permission}
   * @returns {Promise<Map<string, boolean>>}
   */
  const checkPermissions = useCallback(
    async p => VideoSDK.checkPermissions(p),
    []
  );

  /**
   *
   * @param {permission? : Permission}
   * @returns {Promise<Map<string, boolean>>}
   */
  const requestPermission = useCallback(
    async p => VideoSDK.requestPermission(p),
    []
  );

  useEffect(() => {
    const stable = (...args) => onDeviceChangedRef.current(...args);
    VideoSDK.on('device-changed', stable);
    return () => VideoSDK.off('device-changed', stable);
  }, []);

  return {
    getDevices,
    getCameras,
    getMicrophones,
    getPlaybackDevices,
    requestPermission,
    checkPermissions
  };
};

export default useMediaDevice;
